import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Grid } from 'react-bootstrap';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import Game from '../../components/Game';
import { getUserData } from '../../utils/graphQLFunctions';
import fragments from '../../utils/graphql/fragments';
import MobileGameHeader from '../../components/MobileGameHeader';
import cloneDeep from 'lodash/cloneDeep';
import ErrorNotificationPage from 'components/ErrorNotificationPage';
import { SUBSCRIPTION_TOURNAMENT_EVENTS } from 'containers/TournamentList';

const SUBSCRIPTION_UPDATE_DELAY = 4000;

class MobileTournamentPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      showLeaderboard: false,
      tournamentEnded: false
    };

    this.showLeaderboard = this.showLeaderboard.bind(this);
    this.onTournamentEnd = this.onTournamentEnd.bind(this);
    this.onGameStartError = this.onGameStartError.bind(this);
    this.unsubscribeFromTournamentUpdates = () => {};
    this.unsubscribeFromTournamentEvents = () => {};
  }

  componentDidMount() {
    this.unsubscribeFromTournamentUpdates = this.props.subscribeToTournamentUpdates();
    this.unsubscribeFromTournamentEvents = this.props.subscribeToTournamentEvents(
      this.onTournamentEnd
    );
  }

  componentWillUnmount() {
    this.unsubscribeFromTournamentUpdates();
    this.unsubscribeFromTournamentUpdates = () => {};

    this.unsubscribeFromTournamentEvents();
    this.unsubscribeFromTournamentEvents = () => {};
  }

  showLeaderboard(showLeaderboard) {
    this.setState({
      showLeaderboard
    });
  }

  onTournamentEnd(id) {
    const { tournament } = this.props.tournamentData;

    if (id === tournament.id) {
      this.setState({
        tournamentEnded: true
      });
    }
  }

  onGameStartError(message) {
    this.setState({
      error: message
    });
  }

  render() {
    const {
      tournament,
      tournamentScore,
      loading: tournamentLoading
    } = this.props.tournamentData;
    const { showLeaderboard, tournamentEnded } = this.state;

    return (
      <ErrorNotificationPage
        notificationMessage={this.state.error}
        redirectUrl="/mobile/tournaments"
      >
        <Helmet>
          <title>Tournament</title>
        </Helmet>
        <MobileGameHeader
          tournament={tournament}
          toggleLeaderboard={this.showLeaderboard}
          onTournamentEnd={this.onTournamentEnd}
          playerTotalRank={tournamentScore && tournamentScore.totalRank}
          playerScore={tournamentScore && tournamentScore.score}
        />
        <Grid className="main-container tournament-container mobile">
          <div className="tournament-wrapper">
            {tournamentLoading ? (
              <div className="game" />
            ) : !tournamentEnded ? (
              <Game
                isTournament
                gameName={tournament && tournament.game.name}
                tournament={tournament}
                tournamentEnded={tournamentEnded}
                onError={this.onGameStartError}
              />
            ) : (
              <div className="tournament-completed-message">
                <h4>
                  The tournament has completed! Join us next time and find your
                  winning moment!
                </h4>
              </div>
            )}
          </div>
        </Grid>
        {showLeaderboard &&
          tournament &&
          tournament.leaderBoard &&
          tournament.leaderBoard.rankings && (
            <div className="leaderboard mobile">
              <h4>Top Players</h4>
              <ol>
                {tournament.leaderBoard.rankings.map((element, idx) => {
                  return (
                    <li key={idx}>
                      <span className="player">
                        {element.player.displayName}
                      </span>
                      {element.score.toLocaleString()}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
      </ErrorNotificationPage>
    );
  }
}

const QUERY_GET_TOURNAMENT = gql`
  query GetTournament($tournament: GetTournament!) {
    tournament(tournament: $tournament) {
      ...Tournament
      ...TournamentLeaderboard
    }
    tournamentScore(tournament: $tournament) {
      score
      rank
      totalRank
    }
  }
  ${fragments.tournament}
  ${fragments.tournamentLeaderboard}
`;

const SUBSCRIPTION_TOURNAMENT_UPDATES = gql`
  subscription TournamentUpdates($input: TournamentUpdatesInput!) {
    tournamentUpdates(input: $input) {
      leaderBoard {
        rankings {
          player {
            displayName
            userId
            avatarUrl
          }
          score
          rank
        }
      }
      score {
        score
        rank
        totalRank
      }
      totalPlayers
    }
  }
`;

const mapGetTournamentOptions = ({ match }) => ({
  variables: { tournament: { id: match.params.tournament } }
});

const MobileTournamentPageWithData = compose(
  getUserData({
    name: 'userData'
  }),
  graphql(QUERY_GET_TOURNAMENT, {
    name: 'tournamentData',
    options: mapGetTournamentOptions,
    props: ({ ownProps, tournamentData }) => {
      return {
        tournamentData,
        subscribeToTournamentUpdates: () => {
          return tournamentData.subscribeToMore({
            document: SUBSCRIPTION_TOURNAMENT_UPDATES,
            variables: {
              input: { tournamentId: ownProps.match.params.tournament }
            },
            updateQuery: (
              prev,
              {
                subscriptionData: { data: { leaderBoard, score, totalPlayers } }
              }
            ) => {
              if (!leaderBoard && !score && !totalPlayers) {
                return prev;
              }

              setTimeout(() => {
                let prevData = ownProps.client.readQuery({
                  query: QUERY_GET_TOURNAMENT,
                  variables: {
                    tournament: { id: ownProps.match.params.tournament }
                  }
                });

                const newData = cloneDeep(prevData);

                if (leaderBoard) {
                  newData.tournament.leaderBoard = leaderBoard;
                }

                if (score) {
                  newData.tournamentScore = score;
                }

                if (totalPlayers && !score) {
                  newData.tournamentScore.totalRank = `${newData.tournamentScore
                    .rank} / ${totalPlayers}`;
                }

                ownProps.client.writeQuery({
                  query: QUERY_GET_TOURNAMENT,
                  variables: {
                    tournament: { id: ownProps.match.params.tournament }
                  },
                  data: newData
                });
              }, SUBSCRIPTION_UPDATE_DELAY);

              return prev;
            }
          });
        },
        subscribeToTournamentEvents: onEndEvent => {
          return tournamentData.subscribeToMore({
            document: SUBSCRIPTION_TOURNAMENT_EVENTS,
            updateQuery: (
              prev,
              { subscriptionData: { data: { id, event } } }
            ) => {
              if (event === 'end' && onEndEvent) {
                onEndEvent(id);
              }
            }
          });
        }
      };
    }
  })
)(MobileTournamentPage);

export default withApollo(MobileTournamentPageWithData);



// WEBPACK FOOTER //
// ./src/containers/MobileTournamentPage/index.js